import React, { useEffect, useState } from 'react';
import { Text } from '@react-three/drei';
import { useCityStore } from '../../city/cityStore';
import { serverNow } from '../../services/worldSocket';
import { cityTime } from '../../city/cityTime';
import { baseGroundY } from '../../city/cityGround';
import { micWordsAt } from '../../city/stageBridge';

// StageSign — the plaza stage's NEXT SHOW board (Khari, 2026-10-29: "A constant
// 'next show at' message on the stage so users can know when to return and who
// will play").
//
// Always up, beside the stage at its front corner (stage-left), turned to the
// square and in view from the plaza arrival (`?at=plaza`), clear of the
// performer, the LED wall, the speakers, the front benches and the stage's
// cameras. Built like the welcome sign: a dark board on posts, drei Text, a
// little warm light.
//
//   during a set      NOW PLAYING · who · the song (or "on the mic") · who is next
//   before one        NEXT SHOW · when · who
//   nobody booked     NEXT SHOWTIME · when (the stage's blocks, API stageTimes)
//   after the last    NO MORE SHOWS TODAY · back tomorrow at …
//
// Who plays comes from the residents' own days (a set is a segment with `sh`),
// so a set the artist planned and one they accepted both show as soon as they
// exist. Times are the CITY's clock (cityTime), like the HUD and the LED wall.
const AT = [91.4, -33.6];
const ROT = 0.38;            // toward the middle of the square
const S = 0.5;               // board units → world
const CENTER_Y = 0.95;       // board centre above the pavement (world)
const ACCENT = '#ffb35c';
const TEXT = '#f4e2c6';
const DIM = '#b8956b';
// Every time with its zone ("4:15 PM PDT"): the city is Oakland, on West Coast time.
const Z = { zone: true };
// ONE LINE EACH, SIZED TO FIT (2026-10-30: "Malaki The Menace" wrapped onto two
// lines at the big size and ran over NOW PLAYING above and the song below).
// Nothing on the board wraps; a long line gets a smaller size instead. Measured
// by an average glyph width (troika's own measure is async, and a board that
// re-lays itself a frame later flickers): ~0.56 em for these faces.
const LINE_W = 2.6;
const fit = (text, max, min) => Math.max(min, Math.min(max, LINE_W / (Math.max(1, String(text || '').length) * 0.56)));

/** Today's sets on the plaza stage, from everyone's day: [{ a, name, t0, t1 }]. */
function setsFrom(cs) {
  const out = [];
  const seen = new Set();
  for (const r of cs.snap?.residents || []) {
    const segs = cs.byId?.[r.avatarId]?.segs || r.segs || [];
    for (const s of segs) {
      if (!s.sh || (s.p && s.p !== 'street-plaza')) continue;
      const k = `${r.avatarId}:${s.t0}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ a: r.avatarId, name: r.name || 'An artist', t0: s.t0, t1: s.t1 });
    }
  }
  return out.sort((x, y) => x.t0 - y.t0);
}

export function boardFor(cs, t) {
  if (!cs.live) return null;
  const sets = setsFrom(cs);
  const on = sets.find((x) => t >= x.t0 && t < x.t1) || null;
  const next = sets.find((x) => x.t0 > t && (!on || x.t0 >= on.t1)) || null;
  const stage = cs.snap?.stage || null;
  const after = on ? on.t1 : t;
  // The next showtime nobody has taken yet (a slot within ten minutes of a set is that set).
  const openSlot = (stage?.slots || []).find((x) => x >= after && !sets.some((s) => Math.abs(s.t0 - x) < 10 * 60000)) || null;
  const nextLine = next ? `Next: ${cityTime(next.t0, Z)} · ${next.name}` : openSlot ? `Next showtime: ${cityTime(openSlot, Z)}` : '';

  if (on) {
    let what = '';
    const sh = (cs.shows || []).find((x) => x.a === on.a && Math.abs(x.t0 - on.t0) < 10 * 60000);
    if (sh) {
      const words = micWordsAt(sh, t);
      let i = -1;
      for (let k = 0; k < (sh.tracks || []).length; k++) if (t >= sh.t0 + sh.tracks[k].at) i = k;
      const tr = sh.tracks?.[i];
      what = words ? 'on the mic' : tr && t < sh.t0 + tr.at + tr.durMs ? `“${tr.title}”` : '';
    }
    return { label: 'NOW PLAYING', big: on.name, sub: what, foot: nextLine };
  }
  if (next) return { label: 'NEXT SHOW', big: cityTime(next.t0, Z), sub: next.name, foot: openSlot && openSlot < next.t0 ? `Showtime before that: ${cityTime(openSlot, Z)}` : '' };
  if (openSlot) return { label: 'NEXT SHOWTIME', big: cityTime(openSlot, Z), sub: 'the artists decide who plays', foot: '' };
  if (stage?.tomorrow) return { label: 'NO MORE SHOWS TODAY', big: `Back at ${cityTime(stage.tomorrow, Z)}`, sub: 'tomorrow', foot: '' };
  return { label: 'THE STAGE IS QUIET', big: '', sub: '', foot: '' };
}

// `g`: the city ground (CityPlaces useCityGround) — a new one re-renders this onto it.
export default function StageSign({ g = null }) {
  const [b, setB] = useState(() => boardFor(useCityStore.getState(), serverNow()));
  useEffect(() => {
    const tick = () => {
      const next = boardFor(useCityStore.getState(), serverNow());
      setB((cur) => (JSON.stringify(cur) === JSON.stringify(next) ? cur : next));
    };
    const id = setInterval(tick, 1000);
    tick();
    return () => clearInterval(id);
  }, []);
  if (!b) return null;

  const ground = baseGroundY(AT[0], AT[1]);
  const footY = -CENTER_Y / S;      // the pavement, in board units
  return (
    <group position={[AT[0], ground + CENTER_Y, AT[1]]} rotation={[0, ROT, 0]} scale={S}>
      <mesh position={[0, 0, -0.03]}>
        <planeGeometry args={[3.0, 1.9]} />
        <meshBasicMaterial color="#140e08" />
      </mesh>
      <mesh position={[0, 0, -0.02]}>
        <planeGeometry args={[2.88, 1.78]} />
        <meshBasicMaterial color="#1e150c" />
      </mesh>
      <mesh position={[0, 0, -0.06]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[3.0, 1.9]} />
        <meshBasicMaterial color="#0d0a07" />
      </mesh>
      {[-1.1, 1.1].map((x) => (
        <mesh key={x} position={[x, (footY - 0.95) / 2, -0.1]} castShadow>
          <cylinderGeometry args={[0.045, 0.055, -0.95 - footY, 10]} />
          <meshBasicMaterial color="#1b1c20" />
        </mesh>
      ))}

      <Text position={[0, 0.68, 0]} fontSize={0.13} color={DIM} anchorX="center" anchorY="middle" letterSpacing={0.12}>
        THE PLAZA STAGE
      </Text>
      <Text position={[0, 0.42, 0]} fontSize={fit(b.label, 0.15, 0.09)} color={ACCENT} anchorX="center" anchorY="middle" letterSpacing={0.08} whiteSpace="nowrap">
        {b.label}
      </Text>
      {b.big && (
        <Text position={[0, 0.1, 0]} fontSize={fit(b.big, 0.3, 0.12)} color={TEXT} anchorX="center" anchorY="middle" whiteSpace="nowrap">
          {b.big}
        </Text>
      )}
      {b.sub && (
        <Text position={[0, -0.24, 0]} fontSize={fit(b.sub, 0.14, 0.08)} color={ACCENT} anchorX="center" anchorY="middle" whiteSpace="nowrap">
          {b.sub}
        </Text>
      )}
      {b.foot && (
        <Text position={[0, -0.58, 0]} fontSize={fit(b.foot, 0.105, 0.07)} color={DIM} anchorX="center" anchorY="middle" whiteSpace="nowrap">
          {b.foot}
        </Text>
      )}
      {/* No light of its own (2026-10-29): every point light in the scene costs every
          lit pixel on screen, and the plaza stuttered on a phone. Self-lit instead. */}
    </group>
  );
}
